import { useContext, useEffect, useRef, useState } from "react";
import {
  SOCKET_EVENT_TYPES,
  SOCKET_EVENTS,
  SocketContext,
} from "../../components/SocketProvider";
import useCurrentUserData from "../../hooks/useCurrentUserData";
import Game from "../../store/Game";
import INTOUCH_API from "../../services/Api";
import { useLocation, useNavigate } from "react-router-dom";
import ROUTES from "../../utils/routes";
import PersonalAccount from "../../store/PersonalAccount";
import useModalActions from "../../hooks/useModalActions";
import { MODAL_TYPES } from "../../utils/emitter";

export default function useController() {
  const { socket, sendEvent } = useContext(SocketContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isInsightLoading, setIsInsightLoading] = useState(false);
  const { getUserData } = useCurrentUserData();
  const { openModal } = useModalActions();
  const onCheckMessageSocket = (checkedCard) => {
    Game.markCheckedZoneItem(checkedCard);
    checkEndGame();
  };

  // Все карточки отмечены ЧБ → перед запросом отзыва предлагаем +57 (ТЗ #8):
  // один раз на игру, только зарегистрированным и только если набор ещё не разблокирован;
  // отказ ведёт в отзыв, согласие — в покупку (отзыв попросим после прохождения +57).
  // Гейт срабатывает из двух источников на одно действие (socket-хендлер отметки и
  // MobX-эффект по Game.zones) — без one-shot второй вызов навигирует в отзыв
  // поверх только что открытого оффера; переходы дальше делает сама модалка.
  const offerGateFiredRef = useRef(false);
  const goToFeedback = () => {
    if (offerGateFiredRef.current) return;
    offerGateFiredRef.current = true;
    const game = Game.game;
    const offerKey = `wepurplay_ext57_offer_${game?.id}`;
    if (
      game &&
      !game.hasExtendedSet &&
      PersonalAccount.user?.id &&
      !localStorage.getItem(offerKey)
    ) {
      localStorage.setItem(offerKey, "1");
      openModal(MODAL_TYPES.EXT57_OFFER, { gameId: game.id });
      return;
    }
    // Отзыв авто-открываем ОДИН раз на игру: без этого гейт срабатывает при
    // каждом входе с полностью отмеченными зонами и /matches становится
    // недостижимым навсегда (перма-редирект в /feedback).
    const feedbackKey = `wepurplay_feedback_gate_${game?.id}`;
    if (localStorage.getItem(feedbackKey)) return;
    localStorage.setItem(feedbackKey, "1");
    navigate(ROUTES.FEEDBACK_PAGE);
  };

  const checkEndGame = () => {
    // 7-я зона (personal) — анкеты, а не карточки: в гейте отзыва не участвует.
    // Гейт по ФАКТИЧЕСКИ присутствующим в зонах карточкам, а не по теоретическому
    // totalCards: набор +57 могут разблокировать, но пока партнёр не прошёл 57,
    // в зонах только 88 базовых — сравнение с 145 сделало бы отзыв недостижимым.
    const zones = Game.zones.filter((zone) => zone.kind !== "personal");
    const allCards = zones.flatMap((zone) => zone.cards);
    const checkedCount = allCards.filter((card) => card.isCheckedZone).length;

    if (allCards.length > 0 && checkedCount >= allCards.length) goToFeedback();
  };

  const checkCard = (cardInfo) => {
    const user = getUserData();
    const game = Game.game;
    const form = {
      senderId: user.id,
      room: game.id,
      questionId: cardInfo.questionId,
      isChecked: !cardInfo.isCheckedZone,
      zoneKey: cardInfo.zoneKey,
    };
    sendEvent(SOCKET_EVENT_TYPES.ZONE_CARD_CHECK, form);
  };

  const createInsightForm = (card) => {
    setIsInsightLoading(true);
    const game = Game.game;
    const data = {
      gameId: game.id,
      questionId: card.questionId,
    };
    INTOUCH_API.requestManager(
      "createInsightForm",
      data,
      (response) => {
        navigate(ROUTES.INSIGHT_QUESTIONNAIRE_FORM_PAGE(response.data.id), {
          state: {
            formId: response.data.id,
            prevPath: location.pathname,
            from: location.pathname,
          },
        });
      },
      (err) => {
        if (err.status === 400) {
          INTOUCH_API.requestManager(
            "getInsightFormPairByQuestionId",
            { payload: { questionId: card.questionId, gameId: game.id } },
            (response) => {
              const currentUser = getUserData();
              const foundInsightForm = response.data.find(
                (form) => form.userId === currentUser.id
              );
              // 400 мог прийти не только как «анкета уже создана», но и как
              // gameNotFound (игра без партнёра) — тогда пары нет и .id читать нельзя
              if (!foundInsightForm) return;
              if (
                foundInsightForm &&
                foundInsightForm.insightFormAnswers.length
              ) {
                navigate(
                  ROUTES.HISTORY_INSIGHT_QUESTIONNAIRE_FORM_PAGE(
                    foundInsightForm.id
                  ),
                  {
                    state: {
                      formId: foundInsightForm.id,
                    },
                  }
                );
              } else {
                navigate(
                  ROUTES.INSIGHT_QUESTIONNAIRE_FORM_PAGE(foundInsightForm.id),
                  {
                    state: {
                      formId: foundInsightForm.id,
                      prevPath: location.pathname,
                      from: location.pathname,
                    },
                  }
                );
              }
            },
            () => {},
            () => setIsInsightLoading(false)
          );
        }
      },
      () => {
        setIsInsightLoading(false);
      }
    );
  };

  useEffect(() => {
    socket.current.on(
      SOCKET_EVENTS.ZONE_CARD_CHECK_MESSAGE,
      onCheckMessageSocket
    );
    return () => {
      socket.current.off(
        SOCKET_EVENTS.ZONE_CARD_CHECK_MESSAGE,
        onCheckMessageSocket
      );
    };
  }, []);

  useEffect(() => {
    const zones = Game.zones.filter((zone) => zone.kind !== "personal");
    const allCards = zones.flatMap((zone) => zone.cards);
    const checkedCount = allCards.filter((card) => card.isCheckedZone).length;
    // гейт по фактически присутствующим в зонах карточкам (88 или 145 после +57),
    // не по теоретическому totalCards — иначе при непройденном партнёром +57
    // отзыв недостижим; isZonesLoading защищает от пустых зон при загрузке
    if (
      allCards.length > 0 &&
      checkedCount >= allCards.length &&
      !Game.isZonesLoading
    )
      goToFeedback();
  }, [Game.zones]);

  return {
    checkCard,
    createInsightForm,
    isInsightLoading,
    navigate,
  };
}
